"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var d3 = _interopRequireWildcard(require("d3"));
var _useChartDimensions = require("../hooks/useChartDimensions");
var _useChartTooltip = require("../hooks/useChartTooltip");
var _useAxisOverlap = require("../hooks/useAxisOverlap");
var _useChartData = require("../hooks/useChartData");
var _labelFormatter = require("../utils/label-formatter");
var _SvgChart = _interopRequireDefault(require("../components/SvgChart"));
var _BeeswarmChart = require("./BeeswarmChart.styled");
var _useFilter = require("../hooks/useFilter");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const BASE_MARGIN = Object.freeze({
  top: 20,
  bottom: 50,
  left: 20,
  right: 20
});
const AXIS_PADDING = 0.1;
const CIRCLE_RADIUS = 10;
const CIRCLE_PADDING = 1.5;
const BeeswarmChart = /*#__PURE__*/(0, _react.memo)(chartProps => {
  var _chart$colors;
  const {
    chart,
    chartColors,
    animate
  } = chartProps;
  const {
    yCol,
    yFormat
  } = chart;
  const palette = ((_chart$colors = chart.colors) === null || _chart$colors === void 0 ? void 0 : _chart$colors.schemes.categorical) || chartColors.schemes.categorical;
  const {
    filter,
    filterComponent
  } = (0, _useFilter.useFilter)(chartProps);
  const [animationDone, setAnimationDone] = (0, _react.useState)(!animate);
  const [radius, setRadius] = (0, _react.useState)(CIRCLE_RADIUS);
  const [adjustmentCount, setAdjustmentCount] = (0, _react.useState)(0);
  const svgRef = _react.default.useRef(null);
  let {
    width,
    height,
    margin
  } = (0, _useChartDimensions.useChartDimensions)(svgRef, BASE_MARGIN, chart.spanRow, ".x-axis .tick text");
  const {
    detectLabelOverlap,
    labelOverlap
  } = (0, _useAxisOverlap.useAxisOverlap)(svgRef, ".x-axis");
  const {
    data,
    yNums,
    yMin,
    yMax
  } = (0, _useChartData.useChartData)(chart, filter);
  const {
    tooltipRef,
    showTooltip,
    hideTooltip
  } = (0, _useChartTooltip.useChartTooltip)(chart, data, "yCol");
  const yRange = Math.abs(yMax - yMin);
  const yPadding = yRange * AXIS_PADDING;
  const xScale = d3.scaleLinear().domain([yMin < 0 ? yMin - yPadding : Math.max(yMin - yPadding, 0), yMax + yPadding]).range([0, width]);
  const colorPalette = d3.scaleOrdinal().domain(data.map(d => d.seriesKey)).range(palette);

  // Compute the y-positions.
  const pHeights = dodge(yNums.map(num => xScale(num)), radius * 2 + CIRCLE_PADDING);
  const minHeight = Math.max(...pHeights) + (radius + CIRCLE_PADDING) * 2;
  (0, _react.useLayoutEffect)(() => {
    if (svgRef.current === null || height == null) {
      return;
    }
    const mainGroup = d3.select(svgRef.current).select(".main-group");
    const xAxis = d3.axisBottom(xScale).tickSize(labelOverlap ? 5 : 0).tickPadding(labelOverlap ? 2 : 8).tickFormat(v => _labelFormatter.QuantitativeFormatter[yFormat](v.toString()));
    mainGroup.select(".x-axis").attr("transform", "translate(0, ".concat(height, ")")).call(xAxis).selectAll(".tick text").style("text-anchor", labelOverlap ? "end" : "middle");
    detectLabelOverlap();
  }, [height, chart, xScale]);
  (0, _react.useLayoutEffect)(() => {
    if (!height) {
      return;
    }
    // Avoid potential infinite render loop
    if (adjustmentCount > 10) {
      return;
    }
    setAdjustmentCount(count => count + 1);
    if (minHeight > height && radius > 2) {
      setRadius(current => current - 1);
    } else if (minHeight < height * 0.6 && radius < 10) {
      setRadius(current => current + 1);
    }
  }, [chart, height, minHeight, radius]);
  return /*#__PURE__*/_react.default.createElement(_SvgChart.default, {
    margin: margin,
    svgRef: svgRef,
    chart: chart,
    tooltipRef: tooltipRef,
    palette: palette,
    filterComponent: filterComponent
  }, /*#__PURE__*/_react.default.createElement(_BeeswarmChart.BeeswarmChartGroup, {
    className: "beeswarm-chart"
  }, data.map((d, dIdx) => /*#__PURE__*/_react.default.createElement("g", {
    key: d.seriesKey
  }, d.points.map((p, pIdx) => /*#__PURE__*/_react.default.createElement(_react.Fragment, {
    key: "".concat(d.seriesKey, "-").concat(pIdx)
  }, /*#__PURE__*/_react.default.createElement("circle", {
    className: !animationDone ? "" : "full",
    cx: xScale(Number(p[yCol])),
    cy: height - radius - CIRCLE_PADDING - pHeights[pIdx + (dIdx > 0 ? data.slice(0, dIdx).reduce((sum, d) => d.points.length + sum, 0) : 0)],
    r: radius,
    fill: colorPalette(d.seriesKey),
    fillOpacity: 0.8,
    onMouseEnter: e => showTooltip(e.target),
    onMouseLeave: () => hideTooltip(),
    "data-x": p[yCol],
    "data-series": d.seriesKey,
    "data-color": colorPalette(d.seriesKey)
  })))))), /*#__PURE__*/_react.default.createElement("g", {
    className: "x-axis"
  }));
});
var _default = BeeswarmChart;
exports.default = _default;
// Adapted from https://observablehq.com/@d3/beeswarm. Copyright 2021 Observable, Inc.
// Given an array of x-values and a separation radius, returns an array of y-values.
function dodge(xNums, radius) {
  const yNums = new Float64Array(xNums.length);
  const radius2 = radius ** 2;
  const epsilon = 1e-3;
  let head = null;
  let tail = null;

  // Returns true if circle ⟨x,y⟩ intersects with any circle in the queue.
  function intersects(x, y) {
    let a = head;
    while (a) {
      const ai = a.index;
      if (radius2 - epsilon > (xNums[ai] - x) ** 2 + (yNums[ai] - y) ** 2) return true;
      a = a.next;
    }
    return false;
  }

  // Place each circle sequentially.
  for (const bi of d3.range(xNums.length).sort((i, j) => xNums[i] - xNums[j])) {
    // Remove circles from the queue that can’t intersect the new circle b.
    while (head && xNums[head.index] < xNums[bi] - radius2) head = head.next;
    // Choose the minimum non-intersecting tangent.
    if (intersects(xNums[bi], yNums[bi] = 0)) {
      let a = head;
      yNums[bi] = Infinity;
      do {
        const ai = a.index;
        let y = yNums[ai] + Math.sqrt(radius2 - (xNums[ai] - xNums[bi]) ** 2);
        if (y < yNums[bi] && !intersects(xNums[bi], y)) yNums[bi] = y;
        a = a.next;
      } while (a);
    }

    // Add b to the queue.
    const b = {
      index: bi,
      next: null
    };
    if (head === null) head = tail = b;else tail = tail.next = b;
  }
  return yNums;
}